import { useCallback, useRef, useState } from 'react';

import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  TiendaPerfectaVisitHistoryStop,
  TiendaPerfectaVisitHistorySummary,
} from 'models/TiendaPerfecta';
import { User } from 'models/User';

import { DateRange } from '../utils/range';
import {
  VisitSelection,
  aggregateStops,
  stopMatchesSelection,
} from '../utils/selection';
import { SelectionDescription } from '../utils/selectionLabel';
import { ERROR_TOAST_SECONDS } from '../utils/toast';
import {
  buildVisitHistoryWorkbook,
  visitHistoryFileName,
  workbookToBlob,
} from '../utils/xlsxExport';

const REVOKE_DELAY_MS = 1000;

interface UseVisitHistoryExportArgs {
  summary: TiendaPerfectaVisitHistorySummary | null;
  stops: TiendaPerfectaVisitHistoryStop[];
  selection: VisitSelection;
  description: SelectionDescription | null;
  range: DateRange;
  zoneNames: string[];
  distributorNames: string[];
  timeZone: string;
}

const userLabelOf = (user?: Partial<User> | null): string => {
  const name = [user?.name, user?.lastname].filter(Boolean).join(' ');

  return [name, user?.email ? `<${user.email}>` : ''].filter(Boolean).join(' ');
};

/**
 * The `.xlsx` download of the current view: the stops the active selection
 * governs, generated in the browser. Failures are told to the user.
 */
export function useVisitHistoryExport({
  summary,
  stops,
  selection,
  description,
  range,
  zoneNames,
  distributorNames,
  timeZone,
}: UseVisitHistoryExportArgs) {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const authUser = useSelector((state: any) => state.auth) as User;
  const [exporting, setExporting] = useState(false);
  const busy = useRef(false);

  const canDownload = summary !== null && stops.length > 0;

  const download = useCallback(async () => {
    if (!summary || busy.current) return;
    busy.current = true;
    setExporting(true);

    try {
      const filtered = stops.filter((stop) =>
        stopMatchesSelection(stop, selection)
      );
      const workbook = await buildVisitHistoryWorkbook({
        stops: filtered,
        summary,
        aggregated: aggregateStops(filtered, timeZone),
        range,
        zoneNames,
        distributorNames,
        selectionLabel: description
          ? `${t(
              `scenes.visit_history.selection.kinds.${description.kind}`
            )}: ${description.label}`
          : null,
        userLabel: userLabelOf(authUser),
        timeZone,
        generatedAt: new Date(),
        t: (key, options) => t(key, options) as string,
      });
      const blob = await workbookToBlob(workbook);

      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = visitHistoryFileName(range);
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      // Revoked on a later tick: some browsers start the download after
      // `click()` returns and would find the URL already gone.
      setTimeout(() => window.URL.revokeObjectURL(url), REVOKE_DELAY_MS);
    } catch (err) {
      console.error(err);
      message.error(
        t('scenes.visit_history.export.error'),
        ERROR_TOAST_SECONDS
      );
    } finally {
      busy.current = false;
      setExporting(false);
    }
  }, [
    summary,
    stops,
    selection,
    description,
    range,
    zoneNames,
    distributorNames,
    timeZone,
    authUser,
    message,
    t,
  ]);

  return { canDownload, exporting, download };
}
